import { formatRatingIn, toLocale } from '@zuruflex/i18n';
import { Alert, Badge, EmptyState, ErrorState, RatingStars, Skeleton } from '@zuruflex/ui';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { api } from '../lib/api.js';

/**
 * What guests said about this one car.
 *
 * The reviews page shows an owner's whole book at once, which answers "how
 * am I doing" and not "why does nobody book the Vitz". One car's reviews,
 * read together, usually answer the second.
 */
export function CarReviews({ vehicleId }: { vehicleId: string }) {
  const { t, i18n } = useTranslation(['partner', 'common']);
  const locale = toLocale(i18n.language);

  const { data, isPending, error, refetch } = useQuery({
    queryKey: ['vehicle-reviews', vehicleId],
    queryFn: () => api.listReviews({ vehicleId, side: 'GUEST_ON_LISTING', perPage: 100 }),
  });

  if (error) return <ErrorState error={error} onRetry={() => void refetch()} />;
  if (isPending) return <Skeleton className="h-40 w-full rounded-card" />;

  const reviews = data?.items ?? [];
  if (reviews.length === 0) {
    return (
      <EmptyState title={t('carDetail.noReviews')} description={t('carDetail.noReviewsBody')} />
    );
  }

  // Only the published ones carry the average, the same rule the reviews
  // page uses — a review still inside its blind window is not a score yet.
  const scored = reviews.filter((review) => review.status === 'PUBLISHED');
  const average =
    scored.length > 0
      ? Math.round(scored.reduce((total, review) => total + review.overall, 0) / scored.length)
      : null;

  return (
    <>
      {average === null ? null : (
        <div className="mb-4 flex flex-wrap items-center gap-2">
          <RatingStars value={average / 100} size="sm" />
          <span className="text-lg font-semibold">{formatRatingIn(average, locale)}</span>
          <span className="text-sm text-text-muted">
            {t('common:rating.count', { count: scored.length })}
          </span>
        </div>
      )}

      <ul className="flex flex-col gap-3">
        {reviews.map((review) => (
          <li
            key={review.id}
            className="rounded-field border border-border-default px-4 py-3 md:px-4"
          >
            <div className="flex flex-wrap items-center gap-2">
              <RatingStars value={review.overall} size="sm" />
              <span className="font-mono text-xs text-text-muted">{review.bookingReference}</span>
              {review.status === 'PUBLISHED' ? null : (
                <Badge variant="warning">{t(`common:rating.state.${review.status}`)}</Badge>
              )}
            </div>
            <p className="mt-1 text-sm text-text-muted">
              {t('reviews.by', { name: review.authorName })}
            </p>
            {review.body ? <p className="mt-2 whitespace-pre-line text-sm">{review.body}</p> : null}
            {review.hiddenReason ? (
              <Alert variant="warning" className="mt-3">
                {t('reviews.hiddenBecause', { reason: review.hiddenReason })}
              </Alert>
            ) : null}
          </li>
        ))}
      </ul>
    </>
  );
}
